import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { map, tap } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { ApiService } from './api.service';
import { CookieService } from 'ngx-cookie-service';
import { AuthService } from '../auth.service';
import { GenericAppFormIcons } from '@app/shared/models/generic-app';

@Injectable({
  providedIn: 'root',
})
export class AzureBlobStorageService extends ApiService {
  public imgBlobCache: ImgBlob[] = [];

  constructor(public http: HttpClient, public authService: AuthService, private sanitizer: DomSanitizer, private cookieService: CookieService) {
    super(http);
  }

  getBlobImageSafeUrl(path: string): Observable<SafeUrl> {
    const url = this.authService.appToken !== '' ? this.endpoint(`blob`, path) : this.directEndpoint(`blob`, path);
    let response: any;
    const cacheValue = this.imgBlobCache.find((a) => a.key === url);
    if (cacheValue) {
      const blob = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(cacheValue.value));
      return of(blob);
    }

    response = { responseType: 'blob' };

    return this.http.get(url, response).pipe(
      tap((val) => {
        const cacheObj = { key: url, value: val };
        const index = this.imgBlobCache.findIndex((a) => a.key === url);
        if (index >= 0) {
          this.imgBlobCache.splice(index, 1, cacheObj);
        } else {
          this.imgBlobCache.push({ key: url, value: val });
        }
      }),
      map((val: any) => {
        return this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(val));
      })
    );
  }

  getBlobImageUrl(path: string): Observable<SafeUrl> {
    const url = this.authService.appToken !== '' ? this.endpoint(`blob`, path) : this.directEndpoint(`blob`, path);
    let response: any;
    const cacheValue = this.imgBlobCache.find((a) => a.key === url);
    if (cacheValue) {
      const blob = URL.createObjectURL(cacheValue.value);
      return of(blob);
    }
    if (this.authService.appToken !== '') {
      response = { responseType: 'blob' };
    } else {
      response = {
        responseType: 'blob',
      };
    }
    return this.http.get(url, response).pipe(
      tap((val) => {
        const cacheObj = { key: url, value: val };
        const index = this.imgBlobCache.findIndex((a) => a.key === url);
        if (index >= 0) {
          this.imgBlobCache.splice(index, 1, cacheObj);
        } else {
          this.imgBlobCache.push({ key: url, value: val });
        }
      }),
      map((val: any) => {
        return URL.createObjectURL(val);
      })
    );
  }

  getBlobFile(path: string): Observable<Blob> {
    return this.http.get(this.endpoint(`blob`, path), { responseType: 'blob' });
  }

  getSvgFilePreset(path: string): Observable<GenericAppFormIcons> {
    return this.http.get<GenericAppFormIcons>(this.endpoint('api', 'icon', 'preset', `${path}?type=json`), { responseType: 'json' });
  }

  getSvgFileUpload(path: string): Observable<GenericAppFormIcons> {
    return this.http.get<GenericAppFormIcons>(this.endpoint('api', 'icon', 'upload', `${path}?type=json`), { responseType: 'json' });
  }

  uploadSvgFile(file: FormData): Observable<FileDetailsProps> {
    return this.http.post<FileDetailsProps>(this.endpoint('api', 'icon'), file);
  }

  deleteSvgFileUpload(fileName: string): Observable<string> {
    return this.http.delete<string>(this.endpoint('api', `icon?fileName=${fileName}`));
  }

  getFiles(path: string): Observable<any[]> {
    return this.http.get<any[]>(this.endpoint('api', 'blobstorage', `files?path=${path}`));
  }

  getFilesDirect(path: string): Observable<any[]> {
    return this.http.get<any[]>(this.directEndpoint('api', 'blobstorage', `files?path=${path}`));
  }

  scanBlob(path: string, rawFile: File, fileName: string) {
    const formData = new FormData();
    formData.append('Path', path);
    formData.append('File', rawFile, fileName);
    return this.http.post(this.endpoint('api', 'blobstorage', 'scan'), formData);
  }

  uploadBlob(path: string, rawFile: File, fileName: string) {
    const formData = new FormData();
    formData.append('Path', path);
    formData.append('File', rawFile, fileName);
    return this.http.post(this.endpoint('api', 'blobstorage'), formData);
  }

  downloadFile(path: string): Observable<Blob> {
    return this.http.get(this.endpoint('api', 'blobstorage', `?path=${path}`), { responseType: 'blob' });
  }

  downloadFileDirect(path: string): Observable<Blob> {
    return this.http.get(this.directEndpoint('api', 'blobstorage', `?path=${path}`), {
      responseType: 'blob',
    });
  }

  zipFiles(blobPaths: string[]): Observable<any> {
    const body = {
      blobPaths: blobPaths,
    };

    return this.http.post(this.endpoint('api', 'blobstorage', 'zip'), body, { responseType: 'blob' });
  }

  deleteFile = (path: string) => this.http.delete(this.endpoint('api', 'blobstorage', `files?path=${path}`));

  getBlobFileContent(path: string): Observable<SafeUrl> {
    const url = this.directEndpoint(`blob`, path);
    const cacheValue = this.imgBlobCache.find((a) => a.key === url);
    if (cacheValue) {
      const blob = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(cacheValue.value));
      return of(blob);
    }

    return this.http.get(url);
  }

  getBlobFilePath(path: string): string {
    return this.endpoint(`blob`, path);
  }

  getBlobFileDirect(path: string): string {
    return this.directEndpoint(`blob`, path);
  }

  getVariableCss(path: string): Observable<any> {
    return this.http.get(this.endpoint(`blob`, path), { responseType: 'text' });
  }

  updateVariableCss(fileName: string, cssString: string) {
    const blob = new Blob([cssString], { type: 'text/css' });
    const file = new File([blob], fileName);
    const formData = new FormData();
    formData.append('Path', `assets/${fileName}`);
    formData.append('File', file, fileName);
    return this.http.post(this.endpoint('api', 'blobstorage'), formData);
  }
}

interface ImgBlob {
  key: string;
  value: any;
}

export type FileDetailsProps = {
  fileName: string;
  fileSize: number;
  fileType: string;
  lastModified: string;
  iconPath?: string;
  errorCode?: number;
};
